import React, { Component } from "react";
import PropTypes from "prop-types";

import GenericChartComponent from "../GenericChartComponent";
import { getAxisCanvas } from "../GenericComponent";
import { hexToRGBA, getStrokeDasharray, isDefined } from "../utils";

class DarvasBoxSeries extends Component {
  constructor(props) {
    super(props);
    this.renderSVG = this.renderSVG.bind(this);
    this.drawOnCanvas = this.drawOnCanvas.bind(this);
    this.normalizeColor = this.normalizeColor.bind(this);
    this.normalizeStroke = this.normalizeStroke.bind(this);
  }

  normalizeColor(color, fallback) {
    if (typeof color === "string") {
      return color;
    }
    return color?.color || fallback;
  }

  normalizeStroke(stroke, fallback) {
    if (typeof stroke === "string") {
      return { ...fallback, color: stroke };
    }
    return {
      color: stroke?.color || fallback.color,
      width: stroke?.width ?? fallback.width,
      dash: stroke?.dash || fallback.dash,
      opacity: stroke?.opacity ?? fallback.opacity,
    };
  }

  drawOnCanvas(ctx, moreProps) {
    const {
      plotData,
      xScale,
      xAccessor,
      chartConfig: { yScale },
    } = moreProps;
    const {
      yAccessor,
      darvasColor,
      ghostColor,
      levelStroke,
      darvasFillOpacity = 0.25,
      ghostFillOpacity = 0.25,
    } = this.props;

    const darvasColorNormalized = this.normalizeColor(darvasColor, "#5F7CB8");
    const ghostColorNormalized = this.normalizeColor(ghostColor, "#699158");
    const levelStrokeNormalized = this.normalizeStroke(levelStroke, {
      color: "#999999",
      width: 2,
      dash: "ShortDash",
      opacity: 0.8,
    });

    // Draw stop levels first (behind boxes)
    if (levelStrokeNormalized.opacity > 0) {
      this.drawStopLevels(ctx, moreProps, yAccessor, levelStrokeNormalized);
    }

    // Draw boxes
    this.drawBoxes(
      ctx,
      moreProps,
      yAccessor,
      darvasColorNormalized,
      darvasFillOpacity,
      "darvasBox",
    );
    this.drawBoxes(
      ctx,
      moreProps,
      yAccessor,
      ghostColorNormalized,
      ghostFillOpacity,
      "ghostBox",
    );

    // Draw volume spike markers
    this.drawVolumeSpikeMarkers(
      ctx,
      moreProps,
      yAccessor,
      darvasColorNormalized,
    );
  }

  drawStopLevels(ctx, moreProps, yAccessor, stroke) {
    const {
      plotData,
      xScale,
      xAccessor,
      chartConfig: { yScale, width },
    } = moreProps;

    ctx.beginPath();
    ctx.strokeStyle = hexToRGBA(stroke.color, stroke.opacity);
    ctx.lineWidth = stroke.width;
    ctx.setLineDash(
      getStrokeDasharray(stroke.dash)
        .split(",")
        .map((n) => parseInt(n)),
    );

    let prevLossLevel = null;
    for (let i = 0; i < plotData.length; i++) {
      const datum = plotData[i];
      const item = yAccessor(datum);
      if (!item) continue;

      const lossLevel = item.lossLevel;
      if (lossLevel != null && Number.isFinite(lossLevel)) {
        const x = xScale(xAccessor(datum));
        const y = Math.floor(yScale(lossLevel));

        if (prevLossLevel !== null && Math.abs(prevLossLevel - y) < 1) {
          ctx.lineTo(x, y);
        } else {
          if (prevLossLevel !== null) {
            ctx.stroke();
            ctx.beginPath();
          }
          ctx.moveTo(x, y);
        }
        prevLossLevel = y;
      } else {
        if (prevLossLevel !== null) {
          ctx.stroke();
          ctx.beginPath();
        }
        prevLossLevel = null;
      }
    }
    ctx.stroke();
    ctx.setLineDash([]);
  }

  drawBoxes(ctx, moreProps, yAccessor, color, fillOpacity, boxKey) {
    const {
      plotData,
      xScale,
      xAccessor,
      chartConfig: { yScale },
    } = moreProps;

    let activeBox = null;
    let startX = -10;

    for (let i = 0; i < plotData.length; i++) {
      const datum = plotData[i];
      const item = yAccessor(datum);
      if (!item) continue;

      const box = item[boxKey];
      if (box) {
        const x = Math.floor(xScale(xAccessor(datum)));
        const candleWidth = datum.candleWidth || 5;
        const barWidth = Math.floor(Math.max(1, candleWidth));

        // Box start
        if (box.State === 1 && !activeBox) {
          startX = x - barWidth / 2;
          activeBox = { ...box };
        }
        // Box end
        else if (box.State === 0) {
          const endX = x + barWidth / 2;
          const top = Math.floor(yScale(box.High));
          const bottom = Math.floor(yScale(box.Low));
          const height = bottom - top;
          const width = endX - startX;

          // Draw box with stroke and fill
          ctx.strokeStyle = color;
          ctx.fillStyle = hexToRGBA(color, fillOpacity);
          ctx.lineWidth = 1;
          ctx.strokeRect(startX, top, width, height);
          ctx.fillRect(startX, top, width, height);

          activeBox = null;
        }
      }
    }

    // Draw incomplete box (extends to right edge)
    if (activeBox) {
      const lastDatum = plotData[plotData.length - 1];
      if (lastDatum) {
        const x = Math.floor(xScale(xAccessor(lastDatum)));
        const candleWidth = lastDatum.candleWidth || 5;
        const barWidth = Math.floor(Math.max(1, candleWidth));
        const endX = x + barWidth / 2;
        const top = Math.floor(yScale(activeBox.High));
        const bottom = Math.floor(yScale(activeBox.Low));
        const height = bottom - top;
        const width = (endX - startX) * 2; // Extend beyond visible

        // Draw open box (only lines, not closed rectangle)
        ctx.strokeStyle = color;
        ctx.fillStyle = hexToRGBA(color, fillOpacity);
        ctx.lineWidth = 1;

        ctx.beginPath();
        ctx.moveTo(startX + width, top);
        ctx.lineTo(startX, top);
        ctx.lineTo(startX, bottom);
        ctx.lineTo(startX + width, bottom);
        ctx.stroke();

        ctx.fillRect(startX, top, width, height);
      }
    }
  }

  drawVolumeSpikeMarkers(ctx, moreProps, yAccessor, color) {
    const {
      plotData,
      xScale,
      xAccessor,
      chartConfig: { yScale },
    } = moreProps;

    ctx.fillStyle = color;
    ctx.textBaseline = "bottom";
    ctx.font = "16px Arial";
    const signalWidth = ctx.measureText("\u25BC").width / 2;

    for (let i = 0; i < plotData.length; i++) {
      const datum = plotData[i];
      const item = yAccessor(datum);
      if (!item) continue;

      if (item.volumeSpikeMarker === 1) {
        const x = xScale(xAccessor(datum));
        const y = yScale(datum.high);
        ctx.fillText("\u25BC", x - signalWidth, y - 5);
      }
    }
  }

  renderSVG(moreProps) {
    // SVG fallback - simplified version
    const {
      plotData,
      xScale,
      xAccessor,
      chartConfig: { yScale },
    } = moreProps;
    const {
      yAccessor,
      darvasColor,
      ghostColor,
      darvasFillOpacity,
      ghostFillOpacity,
    } = this.props;

    const darvasColorNormalized = this.normalizeColor(darvasColor, "#5F7CB8");
    const ghostColorNormalized = this.normalizeColor(ghostColor, "#699158");

    const boxes = [];

    const renderBoxes = (boxKey, color, opacity) => {
      let activeBox = null;
      let startX = -10;

      for (let i = 0; i < plotData.length; i++) {
        const datum = plotData[i];
        const item = yAccessor(datum);
        if (!item) continue;

        const box = item[boxKey];
        if (box) {
          const x = xScale(xAccessor(datum));
          const candleWidth = datum.candleWidth || 5;
          const barWidth = Math.max(1, candleWidth);

          if (box.State === 1 && !activeBox) {
            startX = x - barWidth / 2;
            activeBox = { ...box };
          } else if (box.State === 0) {
            const endX = x + barWidth / 2;
            const top = yScale(box.High);
            const bottom = yScale(box.Low);
            const height = bottom - top;
            const width = endX - startX;

            boxes.push(
              <rect
                key={`${boxKey}-${i}`}
                x={startX}
                y={top}
                width={width}
                height={height}
                fill={color}
                fillOpacity={opacity}
                stroke={color}
                strokeWidth={1}
              />,
            );

            activeBox = null;
          }
        }
      }
    };

    renderBoxes("darvasBox", darvasColorNormalized, darvasFillOpacity);
    renderBoxes("ghostBox", ghostColorNormalized, ghostFillOpacity);

    return <g>{boxes}</g>;
  }

  render() {
    const { clip } = this.props;
    return (
      <GenericChartComponent
        clip={clip}
        svgDraw={this.renderSVG}
        canvasDraw={this.drawOnCanvas}
        canvasToDraw={getAxisCanvas}
        drawOn={["pan"]}
      />
    );
  }
}

DarvasBoxSeries.propTypes = {
  yAccessor: PropTypes.func.isRequired,
  darvasColor: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  ghostColor: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  levelStroke: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  darvasFillOpacity: PropTypes.number,
  ghostFillOpacity: PropTypes.number,
  clip: PropTypes.bool,
};

DarvasBoxSeries.defaultProps = {
  clip: true,
  darvasColor: "#5F7CB8",
  ghostColor: "#699158",
  levelStroke: {
    color: "#999999",
    width: 2,
    dash: "ShortDash",
    opacity: 0.8,
  },
  darvasFillOpacity: 0.25,
  ghostFillOpacity: 0.25,
};

// Static yExtents method for calculating scale
DarvasBoxSeries.yExtents = (plotData, yAccessor) => {
  if (!plotData || plotData.length === 0) return [0, 1];

  let min = Infinity;
  let max = -Infinity;

  plotData.forEach((d) => {
    if (!d) return;

    const item = yAccessor ? yAccessor(d) : d.darvasBox;
    if (!item) return;

    // Check Darvas boxes
    if (
      item.darvasBox &&
      item.darvasBox.High != null &&
      item.darvasBox.Low != null
    ) {
      min = Math.min(min, item.darvasBox.Low);
      max = Math.max(max, item.darvasBox.High);
    }

    // Check Ghost boxes
    if (
      item.ghostBox &&
      item.ghostBox.High != null &&
      item.ghostBox.Low != null
    ) {
      min = Math.min(min, item.ghostBox.Low);
      max = Math.max(max, item.ghostBox.High);
    }

    // Check stop levels
    if (item.lossLevel != null && Number.isFinite(item.lossLevel)) {
      min = Math.min(min, item.lossLevel);
      max = Math.max(max, item.lossLevel);
    }
    if (item.profitLevel != null && Number.isFinite(item.profitLevel)) {
      min = Math.min(min, item.profitLevel);
      max = Math.max(max, item.profitLevel);
    }
  });

  // If no valid data, return default
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return [0, 1];
  }

  return [min, max];
};

export default DarvasBoxSeries;
